import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageSquare, Loader2, Plus, Trash2, ShieldOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_LABELS = {
  welcome: "Welcome (check-in)",
  callback_nudge: "Callback nudge",
  decline_reminder: "Decline reminder",
  decline_followup: "Decline follow-up",
  customer_message: "Reply to customer",
  thank_you: "Thank-you (check-out)",
  ad_hoc: "Ad-hoc / manual",
};

const PERIODS = [
  { v: 7, l: "7d" }, { v: 30, l: "30d" }, { v: 90, l: "90d" }, { v: 365, l: "12mo" },
];

export default function SmsUsagePage() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optOuts, setOptOuts] = useState([]);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [adding, setAdding] = useState(false);

  const loadStats = useCallback((d = days) => {
    setLoading(true);
    api.get(`/sms/stats?days=${d}`)
      .then((r) => setStats(r.data))
      .catch(() => toast.error("Failed to load SMS stats"))
      .finally(() => setLoading(false));
  }, [days]);

  const loadOptOuts = () => {
    api.get("/sms/opt-outs")
      .then((r) => setOptOuts(r.data.items || []))
      .catch(() => {});
  };

  useEffect(() => { loadStats(days); }, [days, loadStats]);
  useEffect(() => { loadOptOuts(); }, []);

  const addOptOut = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setAdding(true);
    try {
      await api.post("/sms/opt-outs", { phone: phone.trim(), reason: reason.trim() });
      toast.success(`Opted out ${phone.trim()}`);
      setPhone(""); setReason("");
      loadOptOuts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add opt-out");
    } finally { setAdding(false); }
  };

  const removeOptOut = async (p) => {
    try {
      await api.delete(`/sms/opt-outs/${encodeURIComponent(p)}`);
      toast.success("Removed");
      loadOptOuts();
    } catch { toast.error("Couldn't remove"); }
  };

  const cost = (stats?.cost_gbp || 0).toFixed(2);
  const rate = (stats?.rate_gbp_per_segment || 0.04).toFixed(2);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/40 sticky top-0 z-30 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/dashboard?tab=manager" className="btn-outline" data-testid="sms-usage-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="text-right">
            <div className="label-overline">SMS</div>
            <h1 className="font-display font-bold text-lg leading-tight">Usage &amp; opt-outs</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-10">
        {/* Period switch + headline KPIs */}
        <section data-testid="sms-usage-overview">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="label-overline">Period</div>
              <h2 className="font-display font-bold text-xl tracking-tight">
                {days === 7 ? "Last 7 days" : `Last ${days} days`}
              </h2>
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-muted/40 p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setDays(p.v)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                    days === p.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`sms-usage-period-${p.l}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>

          {loading || !stats ? (
            <div className="card-default p-10 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile label="Sent" value={stats.sent} testid="sms-usage-sent" />
                <KpiTile label="Failed" value={stats.failed} testid="sms-usage-failed" />
                <KpiTile label="Segments" value={stats.segments} testid="sms-usage-segments" />
                <KpiTile label="Spend" value={`£${cost}`} accent="primary" footnote={`@ £${rate}/seg`} testid="sms-usage-cost" />
              </div>

              <div className="card-default p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <div className="label-overline">By message type</div>
                </div>
                {(stats.by_kind || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing sent in this period.</p>
                ) : (
                  <div className="space-y-2">
                    {stats.by_kind.map((k) => {
                      const pct = stats.sent ? Math.round((k.sent / stats.sent) * 100) : 0;
                      return (
                        <div key={k.kind} data-testid={`sms-usage-kind-${k.kind}`}>
                          <div className="flex items-center justify-between text-sm">
                            <span>{KIND_LABELS[k.kind] || k.kind}</span>
                            <span className="font-mono-plex text-xs">
                              <span className="text-foreground">{k.sent}</span>
                              <span className="text-muted-foreground/60 ml-3">£{(k.cost_gbp || 0).toFixed(2)}</span>
                              <span className="text-muted-foreground/40 ml-3">{pct}%</span>
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-muted/40 mt-1.5 overflow-hidden">
                            <div className="h-full bg-primary/80" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* Opt-outs (PECR / GDPR) */}
        <section data-testid="sms-usage-opt-outs">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="label-overline text-warning flex items-center gap-1.5">
                <ShieldOff className="w-3.5 h-3.5" /> UK PECR
              </div>
              <h2 className="font-display font-bold text-xl tracking-tight">Customer opt-outs</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                Anyone listed here will be skipped on every SMS dispatch from this workshop — including welcome messages, callback nudges, and follow-ups. Honoured by the backend before Twilio is ever called.
              </p>
            </div>
          </div>

          {/* Add new */}
          <form onSubmit={addOptOut} className="card-default p-5 flex flex-wrap gap-3 items-end" data-testid="sms-opt-out-add-form">
            <div className="flex-1 min-w-[180px]">
              <label htmlFor="opt-phone" className="label-overline">Phone number</label>
              <input
                id="opt-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+447xxxxxxxxx"
                className="input-default font-mono-plex"
                data-testid="sms-opt-out-phone-input"
                required
              />
            </div>
            <div className="flex-[2] min-w-[200px]">
              <label htmlFor="opt-reason" className="label-overline">Reason (optional)</label>
              <input
                id="opt-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Customer requested at counter"
                className="input-default"
                data-testid="sms-opt-out-reason-input"
                maxLength={200}
              />
            </div>
            <button type="submit" disabled={adding} className="btn-primary" data-testid="sms-opt-out-add-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add opt-out
            </button>
          </form>

          {/* List */}
          {optOuts.length === 0 ? (
            <div className="card-default p-8 text-center mt-4 text-muted-foreground" data-testid="sms-opt-out-empty">
              <ShieldOff className="w-8 h-8 mx-auto opacity-60" />
              <p className="text-sm mt-3">No customers have opted out of SMS yet — keep it that way!</p>
            </div>
          ) : (
            <ul className="card-default divide-y divide-border mt-4" data-testid="sms-opt-out-list">
              {optOuts.map((o) => (
                <li key={o.phone} className="px-5 py-3 flex items-center gap-3" data-testid={`sms-opt-out-row-${o.phone}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-plex text-sm">{o.phone}</div>
                    {o.reason && <div className="text-[11px] text-muted-foreground">{o.reason}</div>}
                  </div>
                  <div className="text-[11px] text-muted-foreground/70 font-mono-plex">
                    {o.opted_out_at ? new Date(o.opted_out_at).toLocaleDateString("en-GB") : "—"}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOptOut(o.phone)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove opt-out"
                    data-testid={`sms-opt-out-remove-${o.phone}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 text-[11px] text-muted-foreground/80 flex items-start gap-2 leading-relaxed">
            <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0 mt-0.5" />
            <span>
              Under UK PECR a customer's opt-out must be honoured immediately and without fee. The platform enforces this automatically on every SMS dispatch — adding a number here also blocks any background welcome / callback / reminder send.
            </span>
          </div>
        </section>
      </main>
    </div>
  );
}


function KpiTile({ label, value, accent, footnote, testid }) {
  const color = accent === "primary" ? "text-primary" : "text-foreground";
  return (
    <div className="card-default p-4" data-testid={testid}>
      <div className="label-overline">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tight mt-1 ${color}`}>{value}</div>
      {footnote && <div className="text-[10px] text-muted-foreground/70 mt-0.5">{footnote}</div>}
    </div>
  );
}
